import { type RefObject, useEffect, useRef } from "react";

/** Trap focus only while a modal is open, then restore the invoking control. */
export function useDialogFocus(open: boolean, ref: RefObject<HTMLElement>, onClose: () => void, revision = 0) {
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    if (!open || !ref.current) return;
    const dialog = ref.current;
    const previous = document.activeElement as HTMLElement | null;
    // Hide all branches outside the modal from pointer, keyboard and assistive
    // technology interaction. Restore prior inert state for nested dialogs.
    const isolated: Array<[HTMLElement, boolean]> = [];
    let branch: HTMLElement = dialog;
    while (branch.parentElement) {
      for (const sibling of Array.from(branch.parentElement.children)) {
        if (sibling instanceof HTMLElement && sibling !== branch && !(branch === dialog && sibling.hasAttribute("data-dialog-dismiss")) && !["SCRIPT", "STYLE"].includes(sibling.tagName)) {
          isolated.push([sibling, sibling.inert]);
          sibling.inert = true;
        }
      }
      branch = branch.parentElement;
      if (branch === document.body) break;
    }
    const focusable = () => Array.from(dialog.querySelectorAll<HTMLElement>(
      'button:not(:disabled), a[href], input:not(:disabled), textarea:not(:disabled), select:not(:disabled), [tabindex="0"]',
    )).filter(element => element.getClientRects().length > 0 && !element.closest('[hidden], [inert]') && getComputedStyle(element).visibility !== 'hidden');
    const frame = requestAnimationFrame(() => {
      if (!dialog.contains(document.activeElement)) (focusable()[0] ?? dialog).focus({ preventScroll: true });
    });
    function keydown(event: KeyboardEvent) {
      if (event.key === "Escape") { event.preventDefault(); event.stopPropagation(); close.current(); }
      if (event.key !== "Tab") return;
      const targets = focusable();
      const first = targets[0];
      const last = targets.at(-1);
      if (!first) { event.preventDefault(); dialog.focus({ preventScroll: true }); return; }
      if (event.shiftKey && (document.activeElement === first || !dialog.contains(document.activeElement))) { event.preventDefault(); last?.focus({ preventScroll: true }); }
      else if (!event.shiftKey && (document.activeElement === last || !dialog.contains(document.activeElement))) { event.preventDefault(); first.focus({ preventScroll: true }); }
    }
    dialog.addEventListener("keydown", keydown);
    return () => {
      cancelAnimationFrame(frame);
      dialog.removeEventListener("keydown", keydown);
      for (const [element, wasInert] of isolated) element.inert = wasInert;
      if (previous?.isConnected && !previous.closest("[inert]")) previous.focus({ preventScroll: true });
    };
  }, [open, ref, revision]);
}
